import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, normalizeClass as _normalizeClass, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "grid-item fill-block" }

import { ref, computed, watch, onMounted, nextTick, useTemplateRef } from 'vue';
import { RecycleScroller } from 'vue-virtual-scroller';

import { useRouteIs } from '@services/route-is';
import { windowDims } from '@services/viewport';
import { getPlaceholderClusters } from '@services/dav';
import * as utils from '@services/utils/common';

import Cluster from '@components/frame/Cluster.vue';

import type { ICluster } from '@typings';


export default /*@__PURE__*/_defineComponent({
  __name: 'ClusterGrid',
  props: {
    items: {},
    maxSize: { default: 180 },
    minCols: { default: 3 },
    link: { type: Boolean, default: true },
    plus: { type: Boolean, default: false },
    focus: { type: Boolean, default: false },
    loading: { type: Boolean, default: false }
  },
  emits: ["click", "plus"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;
const recycler = useTemplateRef<VueRecyclerType>('recycler');
const routeIs = useRouteIs();
const recyclerWidth = ref(300);

/** Placeholders to show while loading */
const placeholders: ICluster[] = getPlaceholderClusters(30);

/** Number of items horizontally */
const gridItems = computed(() =>
  // Restrict the number of columns between minCols and the size cap
  Math.max(Math.floor(recyclerWidth.value / props.maxSize), props.minCols),
);

/** Width of the cluster */
const width = computed(() => utils.round(recyclerWidth.value / gridItems.value, 2));

/** Height of the cluster */
const height = computed(() => {
  if (routeIs.Albums) {
    // album view: add gap for text below album
    // 4px extra on mobile for mark#2147915
    return width.value + (windowDims.isMobile ? 46 : 42);
  }

  return width.value;
});

/** Classes list on object */
const classList = computed(() => ({
  empty: !props.items.length && !props.loading,
  'cluster--album': routeIs.Albums,
}));

/** Whether the clusters should show counters */
const counters = computed(() => !routeIs.Albums);

/** List of clusters to display */
const clusters = computed(() => {
  // Show placeholders when loading.
  if (props.loading && !props.items.length) {
    return placeholders;
  }

  // Avoid mutating original props.
  const items = [...props.items];

  // Add plus button if required
  if (props.plus) {
    items.unshift({
      cluster_type: 'plus',
      cluster_id: -1,
      name: '',
      count: 0,
    });
  }

  return items;
});

function click(item: ICluster) {
  switch (item.cluster_type) {
    case 'placeholder':
      return;
    case 'plus':
      emit('plus');
      return;
    default:
      emit('click', item);
  }
}

function resize() {
  recyclerWidth.value = recycler.value?.$el.clientWidth ?? recyclerWidth.value;
}

onMounted(resize);

watch(
  () => props.items,
  async () => {
    if (props.focus) {
      await nextTick();
      recycler.value?.$el.focus();
    }
  },
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(RecycleScroller), {
    ref_key: "recycler",
    ref: recycler,
    tabindex: "1",
    "type-field": "cluster_type",
    "key-field": "cluster_id",
    class: _normalizeClass(["grid-recycler hide-scrollbar-mobile", classList.value]),
    items: clusters.value,
    skipHover: true,
    buffer: 400,
    itemSize: height.value,
    itemSecondarySize: width.value,
    gridItems: gridItems.value,
    onResize: resize
  }, {
    before: _withCtx(() => [
      _renderSlot(_ctx.$slots, "before")
    ]),
    default: _withCtx(({ item }) => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(Cluster, {
          data: item,
          link: __props.link,
          counters: counters.value,
          onClick: ($event: any) => (click(item))
        }, null, 8, ["data", "link", "counters", "onClick"])
      ])
    ]),
    _: 3
  }, 8, ["class", "items", "itemSize", "itemSecondarySize", "gridItems"]))
}
}

})